
import React, {
  useEffect,
  useMemo,
  useState,
} from "react"

import PulseLoader
  from "react-spinners/PulseLoader"

import {
  MagnifyingGlassIcon,
  ArrowPathIcon,
  PencilSquareIcon,
  ChevronLeftIcon,
  ClockIcon,
  CheckCircleIcon,
  UserGroupIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline"

import EtContentEditorLight2
  from "@/utils/EtContentEditorLight2"

import {
  useGetOrgIntentionCandidateCommunicationsQuery,
  useGetOrgIntentionBoardCandidateCommunicationStatusQuery,
  usePreviewOrgIntentionCandidateCommunicationMutation,
  useSendOrgIntentionCandidateCommunicationMutation,
} from "@/features/orgIntentionBoards/orgIntentionCandidateCommunicationsApiSlice"

import {
  COMMUNICATION_DEFAULTS,
} from "@/data/communication/trinityProgramCommunication"

import {
  useGetUserSchedulingPagesQuery,
} from "@/features/userSchedulingPages/userSchedulingPagesApiSlice"


/*
|--------------------------------------------------------------------------
| EtOrgIntentionCandidateCommunications
|--------------------------------------------------------------------------
|
| First Created - 18 Sep 2026
| Author - Atul Pandey
|
| Updated - 25 Sep 2026
| SlotBoard linkage for Initial 1:1 and Deep Dive communications.
|
| Updated - 29 Sep 2026
|
| - Communication Overview is the default workspace.
| - View successful communication coverage by communication type.
| - Candidate search by name or email.
| - Preserve individual candidate History.
| - Compose Communication is a separate workspace.
| - Existing sending, preview, CC and SlotBoard workflows retained.
|
*/


/*
|--------------------------------------------------------------------------
| Communication Configuration
|--------------------------------------------------------------------------
*/

const COMMUNICATION_TYPES = [

  {
    key: "welcome",
    label: "Welcome",
  },

  {
    key: "onboarding_update",
    label: "Pre Program Actions",
  },

  {
    key: "initial_1on1",
    label: "Initial 1:1",
  },

  {
    key: "deep_dive",
    label: "Deep Dive",
  },

]


const getCandidateId =
  candidate => String(
    candidate?.intentionCandidateId
    ||
    candidate?._id
    ||
    candidate?.id
    ||
    ""
  )


const normalizeValue =
  value => String(
    value || ""
  )
    .trim()
    .toLowerCase()


const getCommunicationLabel =
  type => {

    return COMMUNICATION_TYPES.find(
      item => item.key === type
    )?.label || type || "Communication"

  }


const formatDateTime =
  value => {

    if (!value) {
      return ""
    }

    const date =
      new Date(value)

    if (
      Number.isNaN(
        date.getTime()
      )
    ) {
      return ""
    }

    return date.toLocaleString(
      undefined,
      {
        dateStyle: "medium",
        timeStyle: "short",
      }
    )

  }


const isValidEmail =
  value => {

    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
      String(value || "").trim()
    )

  }


/*
|--------------------------------------------------------------------------
| Communication Type Buttons
|--------------------------------------------------------------------------
*/

const CommunicationTypeButtons = ({

  value,

  onChange,

  counts = null,

}) => {

  return (

    <div className="flex flex-wrap gap-2">

      {COMMUNICATION_TYPES.map(
        item => (

          <button

            key={item.key}

            type="button"

            onClick={() =>
              onChange(item.key)
            }

            className={`inline-flex items-center gap-2 rounded px-3 py-2 text-[10px] font-bold transition ${
              value === item.key
                ? "bg-orange-600 text-white"
                : "bg-gray-100 text-gray-600 hover:bg-orange-50"
            }`}

          >

            {item.label}

            {counts && (

              <span
                className={`rounded-full px-2 py-0.5 text-[9px] ${
                  value === item.key
                    ? "bg-white/20 text-white"
                    : "bg-white text-gray-600"
                }`}
              >

                {counts[item.key] || 0}

              </span>

            )}

          </button>

        )
      )}

    </div>

  )

}


/*
|--------------------------------------------------------------------------
| Main Component
|--------------------------------------------------------------------------
*/

const EtOrgIntentionCandidateCommunications = ({

  intentionBoardId,

  orgId,

  candidates = [],

}) => {


  /*
  |--------------------------------------------------------------------------
  | Workspace State
  |--------------------------------------------------------------------------
  */

  const [
    workspaceView,
    setWorkspaceView,
  ] = useState("overview")


  const [
    communicationType,
    setCommunicationType,
  ] = useState("welcome")


  const [
    candidateSearch,
    setCandidateSearch,
  ] = useState("")


  const [
    historyCandidateId,
    setHistoryCandidateId,
  ] = useState(null)


  /*
  |--------------------------------------------------------------------------
  | Recipient Selection
  |--------------------------------------------------------------------------
  */

  const [
    selectedCommunicationCandidateIds,
    setSelectedCommunicationCandidateIds,
  ] = useState([])


  /*
  |--------------------------------------------------------------------------
  | Composer
  |--------------------------------------------------------------------------
  */

  const [
    communicationSubject,
    setCommunicationSubject,
  ] = useState("")


  const [
    communicationMessageContent,
    setCommunicationMessageContent,
  ] = useState("")


  const [
    deliveryMode,
    setDeliveryMode,
  ] = useState("individual")


  const [
    selectedSlotBoardId,
    setSelectedSlotBoardId,
  ] = useState("")


  /*
  |--------------------------------------------------------------------------
  | CC Recipients
  |--------------------------------------------------------------------------
  */

  const [
    ccRecipients,
    setCcRecipients,
  ] = useState([])


  const [
    ccRecipientName,
    setCcRecipientName,
  ] = useState("")


  const [
    ccRecipientEmail,
    setCcRecipientEmail,
  ] = useState("")


  const [
    ccValidationMessage,
    setCcValidationMessage,
  ] = useState("")


  /*
  |--------------------------------------------------------------------------
  | Preview / Send State
  |--------------------------------------------------------------------------
  */

  const [
    welcomePreview,
    setWelcomePreview,
  ] = useState(null)


  const [
    communicationMessage,
    setCommunicationMessage,
  ] = useState("")


  const [
    sendResult,
    setSendResult,
  ] = useState(null)


  /*
  |--------------------------------------------------------------------------
  | Eligible Candidates
  |--------------------------------------------------------------------------
  */

  const communicationCandidates =
    useMemo(
      () => (

        (candidates || [])
          .filter(
            candidate =>
              normalizeValue(
                candidate?.finalDecision
              ) === "go"
          )

      ),
      [candidates]
    )


  /*
  |--------------------------------------------------------------------------
  | Candidate Search - 29 Sep 2026
  |--------------------------------------------------------------------------
  |
  | Search only changes visible candidates.
  | Recipient selections remain intact when the search changes.
  |
  */

  const filteredCommunicationCandidates =
    useMemo(
      () => {

        const search =
          normalizeValue(
            candidateSearch
          )

        if (!search) {
          return communicationCandidates
        }

        return communicationCandidates.filter(
          candidate => {

            const name =
              normalizeValue(
                candidate?.candidateName
              )

            const email =
              normalizeValue(
                candidate?.candidateEmail
              )

            return (
              name.includes(search)
              ||
              email.includes(search)
            )

          }
        )

      },
      [
        communicationCandidates,
        candidateSearch,
      ]
    )


  const selectedCommunicationCandidates =
    communicationCandidates.filter(
      candidate =>
        selectedCommunicationCandidateIds.includes(
          getCandidateId(candidate)
        )
    )


  const allCommunicationCandidatesSelected =
    communicationCandidates.length > 0
    &&
    communicationCandidates.every(
      candidate =>
        selectedCommunicationCandidateIds.includes(
          getCandidateId(candidate)
        )
    )


  const historyCandidate =
    communicationCandidates.find(
      candidate =>
        getCandidateId(candidate) ===
        historyCandidateId
    ) || null


  /*
  |--------------------------------------------------------------------------
  | Board Wide Communication Status - 29 Sep 2026
  |--------------------------------------------------------------------------
  |
  | Reuse the existing board status endpoint.
  | This is the source for the communication overview.
  |
  */

  const {

    data:
      communicationStatusData,

    isLoading:
      isLoadingCommunicationStatus,

    isFetching:
      isFetchingCommunicationStatus,

    isError:
      isCommunicationStatusError,

    error:
      communicationStatusError,

    refetch:
      refetchCommunicationStatus,

  } =
    useGetOrgIntentionBoardCandidateCommunicationStatusQuery(

      {
        orgId,
        intentionBoardId,
      },

      {
        skip:
          !orgId
          ||
          !intentionBoardId,
      }

    )


  /*
  |--------------------------------------------------------------------------
  | Communication Status Map
  |--------------------------------------------------------------------------
  */

  const communicationStatusMap =
    useMemo(
      () => {

        return new Map(

          (
            communicationStatusData?.candidates
            ||
            []
          ).map(
            item => [

              String(
                item?.intentionCandidateId || ""
              ),

              item?.communicationStatus || {},

            ]
          )

        )

      },
      [communicationStatusData]
    )


  /*
  |--------------------------------------------------------------------------
  | Successful Send Detection
  |--------------------------------------------------------------------------
  |
  | The board status endpoint represents the latest milestone status.
  | Its "sent" state indicates a successfully sent communication.
  |
  */

  const hasSentCommunication =
    (
      candidateId,
      type
    ) => {

      const status =
        communicationStatusMap.get(
          String(candidateId)
        )

      return normalizeValue(
        status?.[type]?.status
      ) === "sent"

    }


  /*
  |--------------------------------------------------------------------------
  | Overview Counts
  |--------------------------------------------------------------------------
  */

  const communicationCounts =
    useMemo(
      () => {

        return Object.fromEntries(

          COMMUNICATION_TYPES.map(
            item => [

              item.key,

              communicationCandidates.filter(
                candidate => {

                  const candidateId =
                    getCandidateId(candidate)

                  return normalizeValue(
                    communicationStatusMap
                      .get(candidateId)
                      ?.[item.key]
                      ?.status
                  ) === "sent"

                }
              ).length,

            ]
          )

        )

      },
      [
        communicationCandidates,
        communicationStatusMap,
      ]
    )


  const overviewSentCandidates =
    communicationCandidates.filter(
      candidate =>
        hasSentCommunication(
          getCandidateId(candidate),
          communicationType
        )
    )


  const overviewPendingCandidates =
    communicationCandidates.filter(
      candidate =>
        !hasSentCommunication(
          getCandidateId(candidate),
          communicationType
        )
    )


  /*
  |--------------------------------------------------------------------------
  | Candidate Communication History
  |--------------------------------------------------------------------------
  */

  const {

    data:
      communicationHistoryData,

    isLoading:
      isLoadingCommunicationHistory,

    isFetching:
      isFetchingCommunicationHistory,

    isError:
      isCommunicationHistoryError,

    error:
      communicationHistoryError,

    refetch:
      refetchCommunicationHistory,

  } =
    useGetOrgIntentionCandidateCommunicationsQuery(

      {
        orgId,

        intentionBoardId,

        intentionCandidateId:
          historyCandidateId,
      },

      {
        skip:
          !orgId
          ||
          !intentionBoardId
          ||
          !historyCandidateId,
      }

    )


  const candidateCommunications =
    communicationHistoryData
      ?.communications
      ?.ids
      ?.map(
        id =>
          communicationHistoryData
            ?.communications
            ?.entities
            ?.[id]
      )
      ?.filter(Boolean)
    ||
    []


  const candidateCommunicationHistory =
    candidateCommunications
      .slice()
      .sort(
        (a, b) => (

          new Date(
            b?.createdAt || 0
          ).getTime()
          -
          new Date(
            a?.createdAt || 0
          ).getTime()

        )
      )


  /*
  |--------------------------------------------------------------------------
  | SlotBoards
  |--------------------------------------------------------------------------
  */

  const {

    data:
      userSchedulingPagesData,

    isLoading:
      isLoadingSlotBoards,

    isFetching:
      isFetchingSlotBoards,

    isError:
      isSlotBoardsError,

    error:
      slotBoardsError,

  } =
    useGetUserSchedulingPagesQuery()


  const slotBoards =
    userSchedulingPagesData
      ?.ids
      ?.map(
        id =>
          userSchedulingPagesData
            ?.entities
            ?.[id]
      )
      ?.filter(Boolean)
      ?.filter(
        page =>
          page?.isActive !== false
      )
    ||
    []


  const selectedSlotBoard =
    slotBoards.find(
      page =>
        String(
          page?._id
          ||
          page?.id
          ||
          ""
        ) ===
        String(
          selectedSlotBoardId || ""
        )
    ) || null


  const isSlotBoardCommunication =
    communicationType === "initial_1on1"
    ||
    communicationType === "deep_dive"


  /*
  |--------------------------------------------------------------------------
  | Mutations
  |--------------------------------------------------------------------------
  */

  const [

    previewOrgIntentionCandidateCommunication,

    {
      isLoading:
        isPreviewingCommunication,

      isError:
        isPreviewCommunicationError,

      error:
        previewCommunicationError,

      reset:
        resetPreviewCommunication,
    },

  ] =
    usePreviewOrgIntentionCandidateCommunicationMutation()


  const [

    sendOrgIntentionCandidateCommunication,

    {
      isLoading:
        isSendingCommunication,

      isError:
        isSendCommunicationError,

      error:
        sendCommunicationError,

      reset:
        resetSendCommunication,
    },

  ] =
    useSendOrgIntentionCandidateCommunicationMutation()


  /*
  |--------------------------------------------------------------------------
  | Reset Outcome
  |--------------------------------------------------------------------------
  */

  const resetCommunicationOutcome =
    () => {

      setWelcomePreview(null)

      setCommunicationMessage("")

      setSendResult(null)

    }


  /*
  |--------------------------------------------------------------------------
  | Load Communication Defaults
  |--------------------------------------------------------------------------
  */

  const loadCommunicationDefaults =
    type => {

      const defaults =
        COMMUNICATION_DEFAULTS[type]
        ||
        {
          subject: "",
          messageContent: "",
        }

      setCommunicationSubject(
        defaults.subject || ""
      )

      setCommunicationMessageContent(
        defaults.messageContent || ""
      )

      resetCommunicationOutcome()

    }


  /*
  |--------------------------------------------------------------------------
  | Change Communication Type
  |--------------------------------------------------------------------------
  */

  const handleCommunicationTypeChange =
    type => {

      setCommunicationType(type)

      if (
        type === "welcome"
      ) {
        setDeliveryMode("individual")
      }

      setSelectedSlotBoardId("")

      if (
        workspaceView === "compose"
      ) {
        loadCommunicationDefaults(type)
      }

    }


  /*
  |--------------------------------------------------------------------------
  | Open Composer
  |--------------------------------------------------------------------------
  */

  const handleOpenComposer =
    () => {

      setHistoryCandidateId(null)

      setWorkspaceView("compose")

      if (
        !communicationSubject
        &&
        !communicationMessageContent
      ) {
        loadCommunicationDefaults(
          communicationType
        )
      }

    }


  /*
  |--------------------------------------------------------------------------
  | Open Overview
  |--------------------------------------------------------------------------
  */

  const handleOpenOverview =
    () => {

      setHistoryCandidateId(null)

      setWorkspaceView("overview")

      resetCommunicationOutcome()

    }


  /*
  |--------------------------------------------------------------------------
  | Selection Helpers
  |--------------------------------------------------------------------------
  */

  const handleToggleCommunicationCandidate =
    candidateId => {

      setSelectedCommunicationCandidateIds(
        previous => {

          if (
            previous.includes(candidateId)
          ) {
            return previous.filter(
              id => id !== candidateId
            )
          }

          return [
            ...previous,
            candidateId,
          ]

        }
      )

      resetCommunicationOutcome()

    }


  const handleSelectAllCommunicationCandidates =
    () => {

      setSelectedCommunicationCandidateIds(
        communicationCandidates.map(
          getCandidateId
        ).filter(Boolean)
      )

      resetCommunicationOutcome()

    }


  const handleClearCommunicationCandidates =
    () => {

      setSelectedCommunicationCandidateIds([])

      resetCommunicationOutcome()

    }


  const handleSelectHistoryCandidate =
    candidateId => {

      setHistoryCandidateId(candidateId)

      setWorkspaceView("history")

    }


  /*
  |--------------------------------------------------------------------------
  | CC Helpers
  |--------------------------------------------------------------------------
  */

  const handleAddCcRecipient =
    () => {

      const recipientName =
        ccRecipientName.trim()

      const recipientEmail =
        ccRecipientEmail
          .trim()
          .toLowerCase()


      if (!recipientEmail) {

        setCcValidationMessage(
          "Enter a CC email address."
        )

        return

      }


      if (
        !isValidEmail(
          recipientEmail
        )
      ) {

        setCcValidationMessage(
          "Enter a valid CC email address."
        )

        return

      }


      const alreadyAdded =
        ccRecipients.some(
          recipient =>
            normalizeValue(
              recipient?.recipientEmail
            ) === recipientEmail
        )


      if (alreadyAdded) {

        setCcValidationMessage(
          "This CC recipient has already been added."
        )

        return

      }


      setCcRecipients(
        previous => [

          ...previous,

          {
            recipientName,
            recipientEmail,
          },

        ]
      )

      setCcRecipientName("")

      setCcRecipientEmail("")

      setCcValidationMessage("")

      resetCommunicationOutcome()

    }


  const handleRemoveCcRecipient =
    email => {

      setCcRecipients(
        previous =>
          previous.filter(
            recipient =>
              normalizeValue(
                recipient?.recipientEmail
              ) !==
              normalizeValue(email)
          )
      )

      resetCommunicationOutcome()

    }


  /*
  |--------------------------------------------------------------------------
  | Candidate / Board Reset
  |--------------------------------------------------------------------------
  */

  useEffect(
    () => {

      setWorkspaceView("overview")

      setCommunicationType("welcome")

      setCandidateSearch("")

      setSelectedCommunicationCandidateIds([])

      setHistoryCandidateId(null)

      setCommunicationSubject("")

      setCommunicationMessageContent("")

      setDeliveryMode("individual")

      setSelectedSlotBoardId("")

      setCcRecipients([])

      setCcRecipientName("")

      setCcRecipientEmail("")

      setCcValidationMessage("")

      setWelcomePreview(null)

      setCommunicationMessage("")

      setSendResult(null)

      resetPreviewCommunication()

      resetSendCommunication()

    },
    [
      intentionBoardId,
      resetPreviewCommunication,
      resetSendCommunication,
    ]
  )


  useEffect(
    () => {

      const validIds =
        communicationCandidates.map(
          getCandidateId
        ).filter(Boolean)


      setSelectedCommunicationCandidateIds(
        previous =>
          previous.filter(
            id =>
              validIds.includes(id)
          )
      )


      if (
        historyCandidateId
        &&
        !validIds.includes(
          historyCandidateId
        )
      ) {
        setHistoryCandidateId(null)
      }

    },
    [
      communicationCandidates,
      historyCandidateId,
    ]
  )


  /*
  |--------------------------------------------------------------------------
  | Communication Validation
  |--------------------------------------------------------------------------
  */

  const communicationTextContent =
    (
      communicationMessageContent || ""
    )
      .replace(/<[^>]*>/g, "")
      .replace(/&nbsp;/gi, " ")
      .trim()


  const hasRequiredSlotBoard =
    !isSlotBoardCommunication
    ||
    Boolean(
      selectedSlotBoard?._id
      ||
      selectedSlotBoard?.id
    )


  const canPrepareCommunication =
    Boolean(orgId)
    &&
    Boolean(intentionBoardId)
    &&
    selectedCommunicationCandidateIds.length > 0
    &&
    Boolean(communicationTextContent)
    &&
    hasRequiredSlotBoard


  /*
  |--------------------------------------------------------------------------
  | Build Communication Request
  |--------------------------------------------------------------------------
  */

  const buildCommunicationRequest =
    () => {

      const slotBoardId =
        isSlotBoardCommunication
          ? (
              selectedSlotBoard?._id
              ||
              selectedSlotBoard?.id
              ||
              null
            )
          : null


      const slotBoardPublicSlug =
        isSlotBoardCommunication
          ? (
              selectedSlotBoard?.publicSlug
              ||
              ""
            )
          : ""


      const callToActionLabel =
        communicationType === "initial_1on1"
          ? "Book Initial 1:1"
          : communicationType === "deep_dive"
            ? "Book Deep Dive"
            : ""


      const callToActionUrl =
        isSlotBoardCommunication
        &&
        slotBoardPublicSlug
          ? `${window.location.origin}/trinity/slotboard/${slotBoardPublicSlug}`
          : ""


      return {

        orgId,

        intentionBoardId,

        intentionCandidateIds:
          selectedCommunicationCandidateIds,

        communicationType,

        deliveryMode:
          communicationType === "welcome"
            ? "individual"
            : deliveryMode,

        ccRecipients,

        payload: {

          subject:
            communicationSubject,

          messageContent:
            communicationMessageContent,

          slotBoardId,

          slotBoardPublicSlug,

          callToActionLabel,

          callToActionUrl,

        },

      }

    }


  /*
  |--------------------------------------------------------------------------
  | Preview
  |--------------------------------------------------------------------------
  */

  const handlePreviewCommunication =
    async () => {

      if (!canPrepareCommunication) {
        return
      }

      try {

        setCommunicationMessage("")

        setSendResult(null)

        const result =
          await previewOrgIntentionCandidateCommunication(
            buildCommunicationRequest()
          ).unwrap()

        setWelcomePreview(result)

      } catch (error) {

        console.error(
          "Candidate communication preview error:",
          error
        )

      }

    }


  /*
  |--------------------------------------------------------------------------
  | Send
  |--------------------------------------------------------------------------
  */

  const handleSendCommunication =
    async () => {

      if (!canPrepareCommunication) {
        return
      }


      const recipientCount =
        selectedCommunicationCandidateIds.length


      const effectiveDeliveryMode =
        communicationType === "welcome"
          ? "individual"
          : deliveryMode


      const confirmed =
        window.confirm(

          effectiveDeliveryMode === "group"
            ? `Send one group email to ${recipientCount} selected candidate${recipientCount === 1 ? "" : "s"}?`
            : `Send this communication individually to ${recipientCount} selected candidate${recipientCount === 1 ? "" : "s"}?`

        )


      if (!confirmed) {
        return
      }


      try {

        setCommunicationMessage("")

        setSendResult(null)


        const result =
          await sendOrgIntentionCandidateCommunication(
            buildCommunicationRequest()
          ).unwrap()


        setSendResult(result)


        setCommunicationMessage(

          result?.deliveryMode === "group"
            ? `Group email sent successfully to ${result?.sent || 0} recipient${result?.sent === 1 ? "" : "s"}.`
            : `${result?.sent || 0} communication${result?.sent === 1 ? "" : "s"} sent successfully${result?.failed ? `. ${result.failed} failed.` : "."}`

        )


        setWelcomePreview(null)


        /*
        |--------------------------------------------------------------------------
        | Refresh Board Overview
        |--------------------------------------------------------------------------
        */

        refetchCommunicationStatus()


        /*
        |--------------------------------------------------------------------------
        | Refresh Individual History
        |--------------------------------------------------------------------------
        */

        if (
          historyCandidateId
          &&
          selectedCommunicationCandidateIds.includes(
            historyCandidateId
          )
        ) {
          refetchCommunicationHistory()
        }

      } catch (error) {

        console.error(
          "Candidate communication send error:",
          error
        )

      }

    }


  const failedResults =
    sendResult?.results?.filter(
      result =>
        result?.communicationStatus === "failed"
    ) || []


  /*
  |--------------------------------------------------------------------------
  | Render
  |--------------------------------------------------------------------------
  */

  return (

    <div
      className="
        grid
        grid-cols-1
        lg:grid-cols-12
        gap-1
        m-1
        lg:h-[70vh]
        lg:overflow-hidden
        font-poppins
      "
    >

      {/*
      |--------------------------------------------------------------------------
      | LEFT - Candidate Queue
      |--------------------------------------------------------------------------
      */}

      <div
        className="
          lg:col-span-4
          bg-gray-200
          rounded
          p-1
          lg:h-full
          lg:min-h-0
          lg:flex
          lg:flex-col
          lg:overflow-hidden
        "
      >

        {/*
        |--------------------------------------------------------------------------
        | Candidate Queue Header
        |--------------------------------------------------------------------------
        */}

        <div className="bg-white rounded m-1 p-3 shrink-0">

          <div className="flex justify-between items-start gap-2">

            <div>

              <p className="font-bold text-xs text-gray-800">
                Go Candidates
              </p>

              <p className="text-[9px] text-gray-500 mt-1">
                Select candidates for communication or view their history.
              </p>

            </div>


            {communicationCandidates.length > 0 && (

              <button

                type="button"

                onClick={
                  allCommunicationCandidatesSelected
                    ? handleClearCommunicationCandidates
                    : handleSelectAllCommunicationCandidates
                }

                className="rounded bg-gray-100 text-gray-700 px-3 py-2 text-[9px] font-bold"

              >

                {
                  allCommunicationCandidatesSelected
                    ? "Clear All"
                    : "Select All"
                }

              </button>

            )}

          </div>


          {/*
          |--------------------------------------------------------------------------
          | Candidate Search - 29 Sep 2026
          |--------------------------------------------------------------------------
          */}

          <div className="relative mt-3">

            <MagnifyingGlassIcon
              className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400"
            />

            <input

              type="text"

              value={
                candidateSearch
              }

              onChange={
                event =>
                  setCandidateSearch(
                    event.target.value
                  )
              }

              placeholder="Search by name or email..."

              className="
                w-full
                rounded
                border
                border-gray-200
                bg-gray-50
                pl-9
                pr-8
                py-2
                text-[11px]
                text-gray-700
                outline-none
                focus:border-orange-400
              "

            />

            {candidateSearch && (

              <button

                type="button"

                title="Clear search"

                onClick={() =>
                  setCandidateSearch("")
                }

                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-orange-600"

              >

                <XMarkIcon className="w-4 h-4" />

              </button>

            )}

          </div>


          <div className="flex justify-between items-center gap-2 mt-3">

            <p className="text-[10px] font-bold text-gray-700">

              Selected Recipients: {
                selectedCommunicationCandidateIds.length
              }

            </p>

            <p className="text-[9px] text-gray-400">

              {filteredCommunicationCandidates.length} of {
                communicationCandidates.length
              }

            </p>

          </div>

        </div>


        {/*
        |--------------------------------------------------------------------------
        | Candidate List
        |--------------------------------------------------------------------------
        */}

        <div
          className="
            lg:flex-1
            lg:min-h-0
            lg:overflow-y-auto
          "
        >

          {communicationCandidates.length === 0 && (

            <div className="bg-white rounded m-1 p-3">

              <p className="text-xs text-gray-500">
                There are no candidates with a confirmed Go decision.
              </p>

            </div>

          )}


          {communicationCandidates.length > 0
            &&
            filteredCommunicationCandidates.length === 0
            &&
            (

              <div className="bg-white rounded m-1 p-3">

                <p className="text-xs text-gray-500">
                  No candidates match your search.
                </p>

              </div>

            )
          }


          {filteredCommunicationCandidates.map(
            (candidate, index) => {

              const candidateId =
                getCandidateId(candidate)


              const isSelected =
                selectedCommunicationCandidateIds.includes(
                  candidateId
                )


              const isHistoryCandidate =
                historyCandidateId === candidateId


              const sentCount =
                COMMUNICATION_TYPES.filter(
                  item =>
                    hasSentCommunication(
                      candidateId,
                      item.key
                    )
                ).length


              return (

                <div

                  key={
                    candidateId || index
                  }

                  className={`rounded m-1 p-3 ${
                    isSelected
                      ? "bg-orange-50"
                      : "bg-white"
                  }`}

                >

                  <div className="flex justify-start items-start gap-3">

                    <input

                      type="checkbox"

                      checked={
                        isSelected
                      }

                      onChange={() =>
                        handleToggleCommunicationCandidate(
                          candidateId
                        )
                      }

                      className="mt-1 accent-orange-600"

                    />


                    <div className="flex-1 min-w-0">

                      <p
                        className="text-xs font-bold text-gray-800 leading-snug break-words line-clamp-2"
                        title={candidate?.candidateName}
                      >

                        {candidate?.candidateName}

                      </p>

                      <p
                        className="text-[9px] text-gray-500 mt-1 truncate"
                        title={candidate?.candidateEmail}
                      >

                        {candidate?.candidateEmail}

                      </p>

                    </div>


                    <span className="text-[9px] font-bold rounded-full px-2 py-1 bg-lime-100 text-lime-700">
                      Go
                    </span>

                  </div>


                  {/*
                  |--------------------------------------------------------------------------
                  | Communication Coverage
                  |--------------------------------------------------------------------------
                  */}

                  <div className="flex justify-between items-center gap-2 mt-3 ml-6">

                    <div className="flex items-center gap-1">

                      {COMMUNICATION_TYPES.map(
                        item => {

                          const sent =
                            hasSentCommunication(
                              candidateId,
                              item.key
                            )


                          return (

                            <span

                              key={item.key}

                              title={`${item.label}: ${sent ? "Sent" : "Not Sent"}`}

                              className={`w-2.5 h-2.5 rounded-full ${
                                sent
                                  ? "bg-lime-500"
                                  : "bg-gray-200"
                              }`}

                            />

                          )

                        }
                      )}


                      <span className="text-[9px] text-gray-400 ml-1">

                        {sentCount}/4

                      </span>

                    </div>


                    <button

                      type="button"

                      onClick={() =>
                        handleSelectHistoryCandidate(
                          candidateId
                        )
                      }

                      className={`rounded px-2 py-1 text-[9px] font-bold ${
                        isHistoryCandidate
                          ? "bg-orange-600 text-white"
                          : "bg-gray-100 text-gray-600"
                      }`}

                    >

                      History

                    </button>

                  </div>


                  {/*
                  |--------------------------------------------------------------------------
                  | Candidate Decision
                  |--------------------------------------------------------------------------
                  */}

                  <p className="text-[9px] text-gray-400 mt-2 ml-6">

                    {
                      candidate?.decisionAt
                        ? `Confirmed ${formatDateTime(candidate.decisionAt)}`
                        : "Go decision confirmed"
                    }

                  </p>

                </div>

              )

            }
          )}

        </div>

      </div>


      {/*
      |--------------------------------------------------------------------------
      | RIGHT - Communication Workspace
      |--------------------------------------------------------------------------
      */}

      <div
        className="
          lg:col-span-8
          bg-gray-200
          rounded
          p-1
          lg:h-full
          lg:min-h-0
          lg:overflow-y-auto
        "
      >

        {/*
        |--------------------------------------------------------------------------
        | Workspace Navigation
        |--------------------------------------------------------------------------
        */}

        <div className="bg-white rounded m-1 p-3">

          <div className="flex flex-wrap justify-between items-center gap-3">

            <div className="flex flex-wrap gap-2">

              <button

                type="button"

                onClick={
                  handleOpenOverview
                }

                className={`rounded px-3 py-2 text-[10px] font-bold ${
                  workspaceView === "overview"
                    ? "bg-orange-600 text-white"
                    : "bg-gray-100 text-gray-600"
                }`}

              >

                Communication Overview

              </button>


              <button

                type="button"

                onClick={
                  handleOpenComposer
                }

                className={`inline-flex items-center gap-2 rounded px-3 py-2 text-[10px] font-bold ${
                  workspaceView === "compose"
                    ? "bg-orange-600 text-white"
                    : "bg-gray-100 text-gray-600"
                }`}

              >

                <PencilSquareIcon className="h-4 w-4" />

                Compose Communication

              </button>

            </div>


            {workspaceView === "overview" && (

              <button

                type="button"

                onClick={
                  refetchCommunicationStatus
                }

                disabled={
                  isFetchingCommunicationStatus
                }

                title="Refresh communication status"

                className="rounded bg-orange-50 text-orange-700 p-2 disabled:opacity-40"

              >

                <ArrowPathIcon className="h-4 w-4" />

              </button>

            )}

          </div>

        </div>


        {/*
        |--------------------------------------------------------------------------
        | COMMUNICATION OVERVIEW - DEFAULT VIEW
        |--------------------------------------------------------------------------
        */}

        {workspaceView === "overview" && (

          <div className="bg-white rounded m-1 p-4">

            <div className="flex flex-wrap justify-between items-start gap-3">

              <div>

                <p className="text-sm font-bold text-gray-800">
                  Communication Overview
                </p>

                <p className="text-[10px] text-gray-500 mt-1">
                  See which candidates have received each communication.
                </p>

              </div>

              <span className="rounded-full bg-orange-50 text-orange-700 px-3 py-2 text-[10px] font-bold">

                {communicationCandidates.length} Go Candidates

              </span>

            </div>


            {/*
            |--------------------------------------------------------------------------
            | Type Selection
            |--------------------------------------------------------------------------
            */}

            <div className="mt-5">

              <CommunicationTypeButtons

                value={
                  communicationType
                }

                onChange={
                  handleCommunicationTypeChange
                }

                counts={
                  communicationCounts
                }

              />

            </div>


            {(
              isLoadingCommunicationStatus
              ||
              isFetchingCommunicationStatus
            ) && (

              <div className="py-6 text-center">

                <PulseLoader
                  color="#ea580c"
                  size={4}
                />

              </div>

            )}


            {isCommunicationStatusError && (

              <p className="text-xs text-red-700 mt-4">

                {
                  communicationStatusError
                    ?.data
                    ?.message
                  ||
                  "Unable to load communication status."
                }

              </p>

            )}


            {!isLoadingCommunicationStatus
              &&
              !isFetchingCommunicationStatus
              &&
              !isCommunicationStatusError
              &&
              (

                <>

                  {/*
                  |--------------------------------------------------------------------------
                  | Coverage Metrics
                  |--------------------------------------------------------------------------
                  */}

                  <div className="grid grid-cols-2 gap-2 mt-5">

                    <div className="bg-lime-50 rounded p-4">

                      <p className="text-[9px] font-bold text-lime-700 uppercase">
                        Sent At Least Once
                      </p>

                      <p className="text-2xl font-bold text-gray-800 mt-2">
                        {overviewSentCandidates.length}
                      </p>

                    </div>


                    <div className="bg-gray-50 rounded p-4">

                      <p className="text-[9px] font-bold text-gray-500 uppercase">
                        Not Yet Sent
                      </p>

                      <p className="text-2xl font-bold text-gray-800 mt-2">
                        {overviewPendingCandidates.length}
                      </p>

                    </div>

                  </div>


                  {/*
                  |--------------------------------------------------------------------------
                  | Sent Candidates
                  |--------------------------------------------------------------------------
                  */}

                  <div className="mt-5">

                    <div className="flex items-center justify-between gap-2">

                      <div className="flex items-center gap-2">

                        <CheckCircleIcon className="w-5 h-5 text-lime-600" />

                        <p className="text-xs font-bold text-gray-800">
                          Communication Sent
                        </p>

                      </div>

                      <span className="bg-lime-100 text-lime-700 px-2 py-1 rounded-full text-[9px] font-bold">

                        {overviewSentCandidates.length}

                      </span>

                    </div>


                    {overviewSentCandidates.length === 0 ? (

                      <div className="bg-gray-50 rounded p-4 mt-3">

                        <p className="text-xs text-gray-500">
                          No successful sends recorded for this communication yet.
                        </p>

                      </div>

                    ) : (

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-3">

                        {overviewSentCandidates.map(
                          candidate => {

                            const candidateId =
                              getCandidateId(candidate)


                            const milestone =
                              communicationStatusMap
                                .get(candidateId)
                                ?.[communicationType]


                            return (

                              <div

                                key={candidateId}

                                className="border border-lime-100 bg-lime-50/40 rounded p-3"

                              >

                                <div className="flex justify-between items-start gap-2">

                                  <div className="min-w-0">

                                    <p className="text-xs font-bold text-gray-800 break-words">

                                      {candidate.candidateName}

                                    </p>

                                    <p className="text-[9px] text-gray-500 mt-1 break-all">

                                      {candidate.candidateEmail}

                                    </p>

                                  </div>


                                  <span className="shrink-0 bg-lime-100 text-lime-700 rounded-full px-2 py-1 text-[9px] font-bold">
                                    Sent
                                  </span>

                                </div>


                                {(milestone?.sentAt || milestone?.latestSentAt) && (

                                  <p className="text-[9px] text-gray-500 mt-2">

                                    {formatDateTime(
                                      milestone?.sentAt
                                      ||
                                      milestone?.latestSentAt
                                    )}

                                  </p>

                                )}


                                <button

                                  type="button"

                                  onClick={() =>
                                    handleSelectHistoryCandidate(
                                      candidateId
                                    )
                                  }

                                  className="text-[9px] text-orange-700 font-bold mt-3"

                                >

                                  View History

                                </button>

                              </div>

                            )

                          }
                        )}

                      </div>

                    )}

                  </div>


                  {/*
                  |--------------------------------------------------------------------------
                  | Not Sent Candidates
                  |--------------------------------------------------------------------------
                  */}

                  <div className="mt-6">

                    <div className="flex items-center justify-between gap-2">

                      <div className="flex items-center gap-2">

                        <ClockIcon className="w-5 h-5 text-gray-400" />

                        <p className="text-xs font-bold text-gray-800">
                          Not Yet Sent
                        </p>

                      </div>

                      <span className="bg-gray-100 text-gray-600 rounded-full px-2 py-1 text-[9px] font-bold">

                        {overviewPendingCandidates.length}

                      </span>

                    </div>


                    <div className="flex flex-wrap gap-2 mt-3">

                      {overviewPendingCandidates.map(
                        candidate => (

                          <button

                            key={
                              getCandidateId(candidate)
                            }

                            type="button"

                            title="View candidate history"

                            onClick={() =>
                              handleSelectHistoryCandidate(
                                getCandidateId(candidate)
                              )
                            }

                            className="rounded-full bg-gray-100 text-gray-600 px-3 py-2 text-[10px] font-bold hover:bg-orange-50"

                          >

                            {candidate.candidateName}

                          </button>

                        )
                      )}

                      {overviewPendingCandidates.length === 0 && (

                        <p className="text-xs text-lime-700">
                          All eligible candidates have received this communication.
                        </p>

                      )}

                    </div>

                  </div>


                  {/*
                  |--------------------------------------------------------------------------
                  | Compose Action
                  |--------------------------------------------------------------------------
                  */}

                  <div className="flex justify-end mt-6 pt-4 border-t border-gray-100">

                    <button

                      type="button"

                      onClick={
                        handleOpenComposer
                      }

                      className="inline-flex items-center gap-2 rounded bg-orange-600 text-white px-4 py-2 text-[10px] font-bold"

                    >

                      <PencilSquareIcon className="h-4 w-4" />

                      Compose {
                        getCommunicationLabel(
                          communicationType
                        )
                      }

                    </button>

                  </div>

                </>

              )
            }

          </div>

        )}


        {/*
        |--------------------------------------------------------------------------
        | COMPOSER
        |--------------------------------------------------------------------------
        */}

        {workspaceView === "compose" && (

          <div className="bg-white rounded m-1 p-4">

            <div className="flex justify-between items-start gap-3">

              <div>

                <p className="text-sm font-bold text-gray-800">
                  Candidate Communication
                </p>

                <p className="text-[10px] text-gray-500 mt-1">
                  Compose one common message and choose individual or group delivery.
                </p>

              </div>

              <span className="rounded-full bg-orange-50 text-orange-700 px-3 py-2 text-[9px] font-bold">

                {selectedCommunicationCandidateIds.length} Selected

              </span>

            </div>


            {/*
            |--------------------------------------------------------------------------
            | Selected Recipients
            |--------------------------------------------------------------------------
            */}

            {selectedCommunicationCandidates.length > 0 && (

              <div className="bg-gray-50 rounded p-3 mt-4">

                <p className="text-[9px] font-bold text-gray-500 uppercase">
                  Recipients
                </p>

                <div className="flex flex-wrap gap-1 mt-2">

                  {selectedCommunicationCandidates.map(
                    candidate => (

                      <span

                        key={
                          getCandidateId(candidate)
                        }

                        className="rounded-full bg-white border border-gray-200 px-3 py-1 text-[9px] font-bold text-gray-700"

                      >

                        {candidate.candidateName}

                      </span>

                    )
                  )}

                </div>

              </div>

            )}


            {/*
            |--------------------------------------------------------------------------
            | Communication Type
            |--------------------------------------------------------------------------
            */}

            <div className="mt-4">

              <p className="text-[9px] font-bold text-gray-500 uppercase mb-2">
                Communication Type
              </p>

              <CommunicationTypeButtons

                value={
                  communicationType
                }

                onChange={
                  handleCommunicationTypeChange
                }

              />

            </div>


            {/*
            |--------------------------------------------------------------------------
            | SlotBoard
            |--------------------------------------------------------------------------
            */}

            {isSlotBoardCommunication && (

              <div className="bg-orange-50 rounded p-3 mt-4">

                <p className="text-[9px] font-bold text-orange-700 uppercase">
                  SlotBoard
                </p>

                <p className="text-[9px] text-gray-500 mt-1">

                  Select the SlotBoard candidates should use to book their {
                    getCommunicationLabel(communicationType)
                  }.

                </p>


                {(
                  isLoadingSlotBoards
                  ||
                  isFetchingSlotBoards
                ) && (

                  <div className="mt-3">

                    <PulseLoader
                      color="#ea580c"
                      size={4}
                    />

                  </div>

                )}


                {isSlotBoardsError && (

                  <p className="text-[10px] text-red-700 mt-2">

                    {
                      slotBoardsError?.data?.message
                      ||
                      "Unable to load SlotBoards."
                    }

                  </p>

                )}


                {!isLoadingSlotBoards
                  &&
                  !isFetchingSlotBoards
                  &&
                  !isSlotBoardsError
                  &&
                  (

                    <select

                      value={
                        selectedSlotBoardId
                      }

                      onChange={
                        event => {

                          setSelectedSlotBoardId(
                            event.target.value
                          )

                          resetCommunicationOutcome()

                        }
                      }

                      className="w-full rounded border border-orange-200 bg-white px-3 py-2 mt-3 text-xs text-gray-700 outline-none focus:border-orange-500"

                    >

                      <option value="">
                        Select a SlotBoard
                      </option>


                      {slotBoards.map(
                        page => {

                          const pageId =
                            page?._id
                            ||
                            page?.id

                          return (

                            <option
                              key={pageId}
                              value={pageId}
                            >

                              {
                                page?.pageName
                                ||
                                page?.publicSlug
                                ||
                                "SlotBoard"
                              }

                            </option>

                          )

                        }
                      )}

                    </select>

                  )
                }


                {selectedSlotBoard && (

                  <div className="bg-white rounded p-3 mt-2">

                    <p className="text-[10px] font-bold text-gray-700">

                      {
                        selectedSlotBoard?.pageName
                        ||
                        "Selected SlotBoard"
                      }

                    </p>

                    <p className="text-[9px] text-gray-500 mt-1 break-all">

                      {
                        `${window.location.origin}/trinity/slotboard/${selectedSlotBoard?.publicSlug}`
                      }

                    </p>

                  </div>

                )}

              </div>

            )}


            {/*
            |--------------------------------------------------------------------------
            | Delivery Mode
            |--------------------------------------------------------------------------
            */}

            {communicationType !== "welcome" && (

              <div className="mt-4">

                <p className="text-[9px] font-bold text-gray-500 uppercase">
                  Delivery
                </p>


                <div className="flex flex-wrap gap-2 mt-2">

                  {[
                    {
                      key: "individual",
                      label: "Individual Emails",
                    },
                    {
                      key: "group",
                      label: "One Group Email",
                    },
                  ].map(
                    item => (

                      <button

                        key={item.key}

                        type="button"

                        onClick={() => {

                          setDeliveryMode(item.key)

                          resetCommunicationOutcome()

                        }}

                        className={`rounded px-3 py-2 text-[10px] font-bold ${
                          deliveryMode === item.key
                            ? "bg-orange-600 text-white"
                            : "bg-gray-100 text-gray-600"
                        }`}

                      >

                        {item.label}

                      </button>

                    )
                  )}

                </div>


                <p className="text-[9px] text-gray-500 mt-2">

                  {
                    deliveryMode === "group"
                      ? "One email will be sent to all selected candidates. The greeting will be Hello Team!"
                      : "Each selected candidate will receive an individually addressed email."
                  }

                </p>

              </div>

            )}


            {/*
            |--------------------------------------------------------------------------
            | Subject
            |--------------------------------------------------------------------------
            */}

            <div className="mt-4">

              <label className="block text-[9px] font-bold text-gray-500 uppercase">
                Subject
              </label>

              <input

                type="text"

                value={
                  communicationSubject
                }

                onChange={
                  event => {

                    setCommunicationSubject(
                      event.target.value
                    )

                    resetCommunicationOutcome()

                  }
                }

                placeholder="Enter email subject..."

                className="w-full rounded border border-gray-300 bg-white px-3 py-2 mt-1 text-xs text-gray-800 focus:outline-none focus:ring-1 focus:ring-orange-500"

              />

            </div>


            {/*
            |--------------------------------------------------------------------------
            | CC Recipients
            |--------------------------------------------------------------------------
            */}

            <div className="mt-4">

              <div className="flex justify-between items-center gap-2">

                <div>

                  <p className="text-[9px] font-bold text-gray-500 uppercase">
                    CC Recipients
                  </p>

                  <p className="text-[9px] text-gray-400 mt-1">
                    Optional. These recipients will be copied on the communication.
                  </p>

                </div>

                {ccRecipients.length > 0 && (

                  <span className="text-[9px] font-bold rounded-full px-2 py-1 bg-gray-100 text-gray-600">

                    {ccRecipients.length} CC

                  </span>

                )}

              </div>


              <div className="grid grid-cols-1 md:grid-cols-12 gap-2 mt-2">

                <div className="md:col-span-4">

                  <input

                    type="text"

                    value={
                      ccRecipientName
                    }

                    onChange={
                      event => {

                        setCcRecipientName(
                          event.target.value
                        )

                        setCcValidationMessage("")

                      }
                    }

                    onKeyDown={
                      event => {

                        if (event.key === "Enter") {

                          event.preventDefault()

                          handleAddCcRecipient()

                        }

                      }
                    }

                    placeholder="Name (optional)"

                    className="w-full rounded border border-gray-300 bg-white px-3 py-2 text-xs text-gray-800 outline-none focus:border-orange-500"

                  />

                </div>


                <div className="md:col-span-6">

                  <input

                    type="email"

                    value={
                      ccRecipientEmail
                    }

                    onChange={
                      event => {

                        setCcRecipientEmail(
                          event.target.value
                        )

                        setCcValidationMessage("")

                      }
                    }

                    onKeyDown={
                      event => {

                        if (event.key === "Enter") {

                          event.preventDefault()

                          handleAddCcRecipient()

                        }

                      }
                    }

                    placeholder="Email address"

                    className="w-full rounded border border-gray-300 bg-white px-3 py-2 text-xs text-gray-800 outline-none focus:border-orange-500"

                  />

                </div>


                <div className="md:col-span-2">

                  <button

                    type="button"

                    onClick={
                      handleAddCcRecipient
                    }

                    className="w-full rounded bg-gray-100 text-gray-700 px-3 py-2 text-[10px] font-bold"

                  >

                    + Add CC

                  </button>

                </div>

              </div>


              {ccValidationMessage && (

                <p className="text-[10px] text-red-700 mt-2">

                  {ccValidationMessage}

                </p>

              )}


              {ccRecipients.length > 0 && (

                <div className="flex flex-wrap gap-2 mt-3">

                  {ccRecipients.map(
                    recipient => (

                      <div

                        key={
                          recipient.recipientEmail
                        }

                        className="flex items-center gap-2 rounded-full border border-gray-200 bg-gray-50 px-3 py-1"

                      >

                        <div>

                          {recipient.recipientName && (

                            <p className="text-[9px] font-bold text-gray-700">

                              {recipient.recipientName}

                            </p>

                          )}

                          <p className="text-[9px] text-gray-500">

                            {recipient.recipientEmail}

                          </p>

                        </div>


                        <button

                          type="button"

                          title="Remove CC recipient"

                          onClick={() =>
                            handleRemoveCcRecipient(
                              recipient.recipientEmail
                            )
                          }

                          className="text-[10px] font-bold text-red-600"

                        >

                          ×

                        </button>

                      </div>

                    )
                  )}

                </div>

              )}

            </div>


            {/*
            |--------------------------------------------------------------------------
            | Rich Message
            |--------------------------------------------------------------------------
            */}

            <div className="mt-4">

              <label className="block text-[9px] font-bold text-gray-500 uppercase mb-1">
                Message
              </label>

              <EtContentEditorLight2

                editorKey={
                  `orgIntentionCandidateCommunication-${intentionBoardId}`
                }

                value={
                  communicationMessageContent
                }

                handleChange={
                  value => {

                    setCommunicationMessageContent(
                      value || ""
                    )

                    resetCommunicationOutcome()

                  }
                }

                placeholderText="Write the candidate communication here..."

              />

            </div>


            {/*
            |--------------------------------------------------------------------------
            | Validation / Error / Success
            |--------------------------------------------------------------------------
            */}

            {selectedCommunicationCandidateIds.length === 0 && (

              <p className="text-[10px] text-orange-700 mt-3">
                Select at least one candidate.
              </p>

            )}


            {selectedCommunicationCandidateIds.length > 0
              &&
              !communicationTextContent
              &&
              (

                <p className="text-[10px] text-orange-700 mt-3">
                  Add the communication message before previewing or sending.
                </p>

              )
            }


            {selectedCommunicationCandidateIds.length > 0
              &&
              communicationTextContent
              &&
              isSlotBoardCommunication
              &&
              !selectedSlotBoard
              &&
              (

                <p className="text-[10px] text-orange-700 mt-3">

                  Select the {
                    getCommunicationLabel(communicationType)
                  } SlotBoard before previewing or sending.

                </p>

              )
            }


            {isPreviewCommunicationError && (

              <p className="text-xs text-red-700 mt-3">

                {
                  previewCommunicationError?.data?.message
                  ||
                  "Unable to preview the communication."
                }

              </p>

            )}


            {isSendCommunicationError && (

              <p className="text-xs text-red-700 mt-3">

                {
                  sendCommunicationError?.data?.message
                  ||
                  "Unable to send the communication."
                }

              </p>

            )}


            {communicationMessage && (

              <p className="text-xs text-lime-700 mt-3">

                {communicationMessage}

              </p>

            )}


            {failedResults.length > 0 && (

              <div className="bg-red-50 rounded p-3 mt-3">

                <p className="text-[10px] font-bold text-red-700">
                  Failed Communications
                </p>


                {failedResults.map(
                  (result, index) => {

                    const candidate =
                      communicationCandidates.find(
                        item =>
                          getCandidateId(item) ===
                          String(
                            result?.intentionCandidateId
                          )
                      )


                    return (

                      <p
                        key={
                          result?.intentionCandidateId
                          ||
                          index
                        }
                        className="text-[9px] text-red-700 mt-1"
                      >

                        {
                          candidate?.candidateName
                          ||
                          result?.intentionCandidateId
                          ||
                          "Candidate"
                        }

                        {
                          result?.error?.message
                            ? `: ${result.error.message}`
                            : ""
                        }

                      </p>

                    )

                  }
                )}

              </div>

            )}


            {/*
            |--------------------------------------------------------------------------
            | Actions
            |--------------------------------------------------------------------------
            */}

            <div className="flex justify-end items-center gap-2 mt-4">

              <button

                type="button"

                disabled={
                  isPreviewingCommunication
                  ||
                  isSendingCommunication
                  ||
                  !canPrepareCommunication
                }

                onClick={
                  handlePreviewCommunication
                }

                className="rounded bg-gray-100 text-gray-700 px-4 py-2 text-[10px] font-bold disabled:opacity-50"

              >

                {
                  isPreviewingCommunication
                    ? "Preparing..."
                    : "Preview"
                }

              </button>


              <button

                type="button"

                disabled={
                  isSendingCommunication
                  ||
                  isPreviewingCommunication
                  ||
                  !canPrepareCommunication
                }

                onClick={
                  handleSendCommunication
                }

                className="rounded bg-orange-600 text-white px-4 py-2 text-[10px] font-bold disabled:opacity-50"

              >

                {
                  isSendingCommunication
                    ? "Sending..."
                    : (
                        communicationType !== "welcome"
                        &&
                        deliveryMode === "group"
                      )
                        ? `Send Group Email (${selectedCommunicationCandidateIds.length})`
                        : `Send to ${selectedCommunicationCandidateIds.length}`
                }

              </button>

            </div>


            {/*
            |--------------------------------------------------------------------------
            | Email Preview
            |--------------------------------------------------------------------------
            */}

            {welcomePreview && (

              <div className="border border-gray-200 rounded p-3 mt-5">

                <div className="flex justify-between items-start gap-3">

                  <div>

                    <p className="text-sm font-bold text-gray-800">
                      Email Preview
                    </p>

                    <p className="text-[9px] text-gray-500 mt-1">

                      {
                        welcomePreview?.deliveryMode === "group"
                          ? `Group email preview for ${welcomePreview?.selectedCandidateCount || selectedCommunicationCandidateIds.length} recipients.`
                          : `Previewing for ${welcomePreview?.recipient?.recipientName || "the first selected candidate"}.`
                      }

                    </p>

                  </div>


                  <button

                    type="button"

                    onClick={() =>
                      setWelcomePreview(null)
                    }

                    className="text-[10px] font-bold text-orange-600"

                  >

                    Close Preview

                  </button>

                </div>


                <div className="bg-gray-100 rounded p-2 mt-3">

                  <div className="bg-white rounded p-3 mb-2">

                    <p className="text-[9px] font-bold text-gray-500 uppercase">
                      Subject
                    </p>

                    <p className="text-xs text-gray-800 mt-1">

                      {welcomePreview?.subject}

                    </p>

                  </div>


                  {welcomePreview?.ccRecipients?.length > 0 && (

                    <div className="bg-white rounded p-3 mb-2">

                      <p className="text-[9px] font-bold text-gray-500 uppercase">
                        CC
                      </p>

                      <div className="flex flex-wrap gap-1 mt-2">

                        {welcomePreview.ccRecipients.map(
                          recipient => (

                            <span

                              key={
                                recipient?.recipientEmail
                              }

                              className="text-[9px] rounded-full bg-gray-100 text-gray-600 px-2 py-1"

                            >

                              {
                                recipient?.recipientName
                                  ? `${recipient.recipientName} <${recipient.recipientEmail}>`
                                  : recipient?.recipientEmail
                              }

                            </span>

                          )
                        )}

                      </div>

                    </div>

                  )}


                  <iframe

                    title="Candidate Communication Email Preview"

                    srcDoc={
                      welcomePreview?.html || ""
                    }

                    sandbox=""

                    className="w-full h-[600px] bg-white rounded border-0"

                  />

                </div>

              </div>

            )}

          </div>

        )}


        {/*
        |--------------------------------------------------------------------------
        | INDIVIDUAL CANDIDATE HISTORY
        |--------------------------------------------------------------------------
        */}

        {workspaceView === "history" && (

          <div className="bg-white rounded m-1 p-4">

            <div className="flex justify-between items-start gap-3">

              <div>

                <p className="text-sm font-bold text-gray-800">
                  Communication History
                </p>

                <p className="text-[10px] text-gray-500 mt-1">

                  {
                    historyCandidate?.candidateName
                    ||
                    "Candidate"
                  }

                </p>

              </div>


              <button

                type="button"

                onClick={
                  handleOpenOverview
                }

                className="inline-flex items-center gap-1 text-[10px] font-bold text-orange-700"

              >

                <ChevronLeftIcon className="h-4 w-4" />

                Overview

              </button>

            </div>


            {historyCandidate && (

              <div className="bg-gray-50 rounded p-3 mt-3">

                <p className="text-xs font-bold text-gray-700">

                  {historyCandidate.candidateName}

                </p>

                <p className="text-[9px] text-gray-500 mt-1">

                  {historyCandidate.candidateEmail}

                </p>

              </div>

            )}


            {(
              isLoadingCommunicationHistory
              ||
              isFetchingCommunicationHistory
            ) && (

              <div className="mt-4">

                <PulseLoader
                  color="#ea580c"
                  size={4}
                />

              </div>

            )}


            {isCommunicationHistoryError && (

              <p className="text-xs text-red-700 mt-3">

                {
                  communicationHistoryError?.data?.message
                  ||
                  "Unable to load candidate communication history."
                }

              </p>

            )}


            {!isLoadingCommunicationHistory
              &&
              !isFetchingCommunicationHistory
              &&
              !isCommunicationHistoryError
              &&
              candidateCommunicationHistory.length === 0
              &&
              (

                <div className="bg-gray-50 rounded p-4 mt-3">

                  <p className="text-xs text-gray-500">
                    No communications have been recorded for this candidate.
                  </p>

                </div>

              )
            }


            {!isLoadingCommunicationHistory
              &&
              !isFetchingCommunicationHistory
              &&
              candidateCommunicationHistory.map(
                (communication, index) => (

                  <div

                    key={
                      communication?._id
                      ||
                      index
                    }

                    className="bg-gray-50 rounded p-3 mt-2"

                  >

                    <div className="flex justify-between items-start gap-2">

                      <div className="min-w-0">

                        <p className="text-xs font-bold text-gray-700 break-words">

                          {
                            communication?.subject
                            ||
                            "Communication"
                          }

                        </p>


                        <p className="text-[9px] text-gray-500 mt-1">

                          {
                            getCommunicationLabel(
                              communication?.communicationType
                            )
                          }

                          {
                            communication?.metadata?.deliveryMode === "group"
                              ? " · Group Email"
                              : " · Individual Email"
                          }

                        </p>


                        {communication?.sentAt && (

                          <p className="text-[9px] text-gray-500 mt-1">

                            Sent: {
                              formatDateTime(
                                communication.sentAt
                              )
                            }

                          </p>

                        )}


                        {communication?.ccRecipients?.length > 0 && (

                          <p className="text-[9px] text-gray-500 mt-1 break-words">

                            CC: {
                              communication.ccRecipients
                                .map(
                                  recipient =>
                                    recipient?.recipientName
                                      ? `${recipient.recipientName} <${recipient.recipientEmail}>`
                                      : recipient?.recipientEmail
                                )
                                .filter(Boolean)
                                .join(", ")
                            }

                          </p>

                        )}

                      </div>


                      <span
                        className={`shrink-0 text-[9px] font-bold rounded-full px-2 py-1 ${
                          communication?.communicationStatus === "sent"
                            ? "bg-lime-100 text-lime-700"
                            : communication?.communicationStatus === "failed"
                              ? "bg-red-100 text-red-700"
                              : "bg-gray-100 text-gray-600"
                        }`}
                      >

                        {
                          communication?.communicationStatus
                          ||
                          "draft"
                        }

                      </span>

                    </div>

                  </div>

                )
              )
            }

          </div>

        )}

      </div>

    </div>

  )

}


export default EtOrgIntentionCandidateCommunications